import { useScrollReveal } from "../hooks/useScrollReveal";

const About = () => {
  const { ref: sectionRef } = useScrollReveal();
  const { ref: imageRef, isVisible: imageVisible } = useScrollReveal({
    threshold: 0.2,
  });
  const { ref: contentRef, isVisible: contentVisible } = useScrollReveal({
    threshold: 0.2,
    rootMargin: "0px 0px -80px 0px",
  });

  const highlights = [
    { number: "10+", label: "Years Coaching" },
    { number: "300+", label: "Athletes Trained" },
    { number: "200+", label: "National Players" },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-24 bg-[var(--bg-primary)] overflow-hidden"
    >
      {/* Background Decorative Orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-orange-400/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center">
          {/* Image Side */}
          <div
            ref={imageRef}
            className={`relative transition-all duration-700 ${
              imageVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-10"
            }`}
          >
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-orange-500/10">
                <img
                  src="https://res.cloudinary.com/dbfyzgffj/image/upload/v1758905396/Whats-App-Image-2025-06-08-at-6-17-03-PM-1_ejqwjm.jpg"
                  alt="OCSSC Training Session"
                  className="w-full h-64 sm:h-80 md:h-[28rem] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>

              {/* Floating Stats Card */}
              <div
                className={`absolute -bottom-5 right-2 sm:right-4 md:right-6 bg-white dark:bg-gray-900 rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-5 shadow-xl border border-[var(--border-primary)] transition-all duration-700 delay-300 ${
                  imageVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.166 2.621v.858c-1.035.148-2.059.33-3.071.543a.75.75 0 00-.584.859 6.753 6.753 0 006.138 5.6 6.73 6.73 0 002.743 1.346A6.707 6.707 0 019.279 15H8.54c-1.036 0-1.875.84-1.875 1.875V19.5h-.75a.75.75 0 000 1.5h12.17a.75.75 0 000-1.5h-.75v-2.625c0-1.036-.84-1.875-1.875-1.875h-.739a6.707 6.707 0 01-1.112-3.173 6.73 6.73 0 002.743-1.347 6.753 6.753 0 006.139-5.6.75.75 0 00-.585-.858 47.077 47.077 0 00-3.07-.543V2.62a.75.75 0 00-.658-.744 49.22 49.22 0 00-6.093-.377c-2.063 0-4.096.128-6.093.377a.75.75 0 00-.657.744z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[var(--text-primary)]">
                      200+
                    </div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      National Players
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Badge */}
              <div
                className={`absolute -top-3 left-3 sm:left-4 md:left-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl md:rounded-2xl px-3 py-2 md:px-4 md:py-3 shadow-lg shadow-orange-500/30 transition-all duration-700 delay-200 ${
                  imageVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 -translate-y-4"
                }`}
              >
                <div className="text-sm font-bold">Since 2014</div>
                <div className="text-xs text-orange-100">Nagpur, India</div>
              </div>
            </div>
          </div>

          {/* Content Side */}
          <div
            ref={contentRef}
            className={`transition-all duration-700 delay-200 ${
              contentVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-10"
            }`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 text-xs font-semibold uppercase tracking-wider mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
              About Us
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-6 gowun-batang leading-tight">
              Where Champions Are <span className="text-orange-500">Made</span>
            </h2>

            <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed mb-6">
              Orange City Sport Shooting Club is Nagpur's premier sport shooting
              training academy, dedicated to nurturing young talent and
              producing national-level champions in air rifle and air pistol
              disciplines.
            </p>

            <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed mb-8">
              Under the expert guidance of{" "}
              <span className="font-semibold text-orange-500">
                Coach Anil Pande
              </span>
              , our athletes have consistently excelled at CBSE Nationals, SGFI
              Championships, Khelo India games, and National selection trials.
            </p>

            {/* Highlights */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {highlights.map((item, i) => (
                <div
                  key={i}
                  className="text-center p-2.5 sm:p-3 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-secondary)]"
                >
                  <div className="text-lg sm:text-xl md:text-2xl font-bold text-orange-500">
                    {item.number}
                  </div>
                  <div className="text-[10px] sm:text-xs text-[var(--text-tertiary)] mt-1">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  const el = document.getElementById("achievements");
                  if (el)
                    window.scrollTo({
                      top: el.offsetTop - 80,
                      behavior: "smooth",
                    });
                }}
                className="btn-primary"
              >
                Meet Our Champions
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                  />
                </svg>
              </button>
              <button
                onClick={() => {
                  const el = document.getElementById("performance_support");
                  if (el)
                    window.scrollTo({
                      top: el.offsetTop - 80,
                      behavior: "smooth",
                    });
                }}
                className="btn-secondary"
              >
                Our Programs
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
